import React from 'react';
import { GuidedProgressBar } from './GuidedProgressBar';

export interface GuidedSessionCompleteProps {
  firstName: string;
  total: number;
  /** Researcher's closing message. No admin field exists for this today
      (Project type only has welcomeTitle/welcomeMessage); a fallback is
      shown until Project.closingMessage is added. */
  closingMessage?: string;
}

const DEFAULT_CLOSING =
  'That was beautiful. We’ll be back with a summary in a few days.';

/** Final ceremony of a one_time study — all tasks submitted. A true
    terminal screen: nothing to tap, nothing routes back into the app.
    A one-time study is fire-and-forget — the researcher reviews the
    data, not the participant — so there is no submissions archive
    here. Delivery status is carried by the UploadBar (rendered by the
    guided shell); this screen only needs to feel finished.
    Per design decisions: no scattered specks (#5); no submissions CTA
    (one_time has no participant-facing review). */
export const GuidedSessionComplete: React.FC<GuidedSessionCompleteProps> = ({
  firstName,
  total,
  closingMessage,
}) => {
  const message = closingMessage?.trim() || DEFAULT_CLOSING;
  const noun = total === 1 ? 'reflection' : 'reflections';

  return (
    <div className="gd-screen gd-screen--complete">
      <div className="gd-progress-strip">
        <GuidedProgressBar total={total} current={total} />
      </div>

      <div className="gd-greeting">
        <h1>
          Thank you,
          <br />
          <span className="gd-name">{firstName || 'friend'}.</span>
        </h1>
        <p className="gd-quote">&ldquo;{message}&rdquo;</p>
      </div>

      <div className="gd-bottom" style={{ alignItems: 'center' }}>
        {/* Quiet closure line — reassurance, not a scoreboard or a
            navigation target. Sage check echoes the "all done" feel. */}
        <div className="gd-complete-eyebrow">
          <svg
            className="gd-complete-eyebrow-check"
            width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>All {total} {noun} safely sent</span>
        </div>
      </div>
    </div>
  );
};
